import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { authClient, useSession } from "@/frontend/auth-client";
import { SCOPE_ROWS, ALWAYS_GRANTED_API_SCOPES } from "@/frontend/constants/oauth-scopes";
import { Button } from "@/frontend/components/ui/button";
import { Checkbox } from "@/frontend/components/ui/checkbox";
import { ClientLogo } from "@/frontend/components/shared/ClientBrand";
import { displayClientName } from "@/frontend/utils/client-brand";

const IDENTITY_SCOPES = ["openid", "profile", "email", "offline_access"];

/** Scopes granted without asking: identity plus the always-on API scopes. */
const SILENT_SCOPES = [...IDENTITY_SCOPES, ...ALWAYS_GRANTED_API_SCOPES];

/**
 * OAuth consent screen, rendered as a centered dialog. Reads the requested
 * scopes from the signed authorize query in the URL, shows one tick box per
 * grantable capability with an inline description, and answers via
 * authClient.oauth2.consent (the client plugin carries the signed query).
 * Identity and workspace-visibility scopes are granted silently.
 */
export function ConsentView() {
  const { data: session } = useSession();
  // The router's custom stringifySearch preserves the signed query, so the live
  // URL is safe to read directly (see utils/search-params.ts).
  const params = new URLSearchParams(window.location.search);
  const clientId = params.get("client_id") ?? "";
  const requested = new Set((params.get("scope") ?? "").split(" ").filter(Boolean));

  const rows = SCOPE_ROWS.filter((r) => r.scopes.some((s) => requested.has(s)));
  const [checked, setChecked] = useState<Record<string, boolean>>(
    Object.fromEntries(rows.map((r) => [r.key, r.defaultChecked])),
  );
  const [submitting, setSubmitting] = useState<"allow" | "deny" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: client } = useQuery({
    queryKey: ["oauth-public-client", clientId],
    queryFn: async () => {
      const res = await authClient.oauth2.publicClient({ query: { client_id: clientId } });
      if (res.error) throw new Error(res.error.message);
      return res.data;
    },
    enabled: clientId.length > 0,
  });

  /** Posts the consent decision and follows the returned redirect. */
  async function respond(accept: boolean) {
    setSubmitting(accept ? "allow" : "deny");
    setError(null);
    const grantedScopes = accept
      ? [
          ...SILENT_SCOPES.filter((s) => requested.has(s)),
          ...rows.filter((r) => checked[r.key]).flatMap((r) => r.scopes.filter((s) => requested.has(s))),
        ]
      : undefined;
    const res = await authClient.oauth2.consent(
      accept ? { accept: true, scope: grantedScopes?.join(" ") } : { accept: false },
    );
    if (res.error || !res.data?.url) {
      setSubmitting(null);
      setError(res.error?.message ?? "Something went wrong. Try again.");
      return;
    }
    window.location.href = res.data.url;
  }

  // The public-client endpoint returns RFC 7591 fields (client_name, logo_uri);
  // the better-auth client infers them loosely, so narrow at runtime, no cast.
  const record: Record<string, unknown> = client ?? {};
  const clientName = displayClientName(typeof record.client_name === "string" ? record.client_name : null);
  const iconUrl = typeof record.logo_uri === "string" && record.logo_uri.length > 0 ? record.logo_uri : null;

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
      <div className="grid w-full max-w-4xl overflow-hidden rounded-2xl border border-border bg-background shadow-lg md:grid-cols-[1fr_1.15fr]">
        {/* Left: who is asking. Medblocks wordmark as the page header, then the
            client logo attached to the title (top-aligned), footnote at the bottom. */}
        <div className="flex flex-col p-6 md:border-r md:border-border md:p-10">
          <img src="/assets/Medblocks_Logo.png" alt="Medblocks" className="h-6 self-start object-contain opacity-90" />
          <div className="mt-12 flex flex-col gap-4">
            <ClientLogo name={clientName} logoUrl={iconUrl} className="size-12" />
            <h1 className="text-2xl font-bold leading-tight tracking-tight">
              {clientName} wants to access your Medblocks workspace
            </h1>
            <p className="text-sm text-muted-foreground">
              Signed in as {session?.user?.name ?? session?.user?.email}
            </p>
          </div>
          <p className="mt-auto pt-10 text-xs text-muted-foreground">
            {clientName} will always know who you are (your name and email).
          </p>
        </div>

        {/* Right: what it can do + decision */}
        <div className="flex flex-col border-t border-border p-6 md:border-t-0 md:p-10">
          <p className="text-sm font-medium text-muted-foreground">Choose what {clientName} can do</p>
          <div className="mt-6 flex flex-col gap-7">
            {rows.map((row) => {
              const isChecked = checked[row.key] ?? false;
              return (
                <label key={row.key} htmlFor={`scope-${row.key}`} className="flex cursor-pointer items-start gap-4">
                  <Checkbox
                    id={`scope-${row.key}`}
                    className="mt-0.5"
                    checked={isChecked}
                    onCheckedChange={() => setChecked((c) => ({ ...c, [row.key]: !c[row.key] }))}
                  />
                  <span className="flex flex-col gap-1.5">
                    <span className="text-sm font-semibold leading-none">{row.label}</span>
                    <span className="text-sm leading-relaxed text-muted-foreground">{row.description}</span>
                  </span>
                </label>
              );
            })}
          </div>

          {error ? <p className="mt-4 text-sm text-destructive">{error}</p> : null}

          <div className="mt-10 flex gap-3">
            <Button variant="outline" className="flex-1" disabled={submitting !== null} onClick={() => respond(false)}>
              {submitting === "deny" ? <Loader2 className="size-4 animate-spin" /> : "Deny"}
            </Button>
            <Button className="flex-1" disabled={submitting !== null} onClick={() => respond(true)}>
              {submitting === "allow" ? <Loader2 className="size-4 animate-spin" /> : "Allow"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
